import { Tooltip } from '@heroui/react'
import { useMutation } from '@tanstack/react-query'
import { Loading } from 'components/core/Loading'
import { Button } from 'components/ui/Button'
import { AlertCircle, Languages, ThumbsDown, ThumbsUp } from 'lucide-react'
import { useEffect, useState } from 'react'
import { $fetch } from 'utils/fetch'
import { fetchAndUpdateTranscriptStatus } from '../../../utils/funcs/call-logs/fetchTranscript'

export default function CallTranscript({ initialTranscripts, loadingTranscript, callId }) {
  const [transcripts, setTranscripts] = useState([])
  const [translatedTranscripts, setTranslatedTranscripts] = useState([])
  const [isTranslationMode, setIsTranslationMode] = useState(false)
  const [hasTranslations, setHasTranslations] = useState(false)
  const [detectedLanguage, setDetectedLanguage] = useState()

  useEffect(() => {
    setTranscripts(initialTranscripts)
  }, [initialTranscripts])

  const { mutate: detectLanguage, isPending: loadingDetection } = useMutation({
    mutationFn: async () => {
      // Prepare the payload for language detection
      const payload = transcripts.map(transcript => ({
        id: transcript.id.toString(),
        text: transcript.text,
      }))

      const response = await $fetch(`/v1/translate/detect`, {
        method: 'POST',
        body: payload,
      })
      return response?.data
    },
    onSuccess: (data) => {
      setDetectedLanguage(data?.language)
    },
  })

  useEffect(() => {
    if (transcripts?.length > 0) {
      detectLanguage()
    }
  }, [transcripts])
  const showTranslateButton = !!detectedLanguage && detectedLanguage !== 'en'

  const { mutate: extractTranslation, isPending: loadingTranslating } = useMutation({
    mutationFn: async () => {
      // Prepare the payload as expected by the backend
      const payload = transcripts.map(transcript => ({
        id: transcript.id.toString(),
        text: transcript.text,
      }))

      const response = await $fetch(`/v1/translate`, {
        method: 'POST',
        body: payload,
      })
      return response?.data
    },
    onSuccess: (data) => {
      // Convert array response to object for easier lookup
      const translationsMap = {}
      data.forEach((translation) => {
        translationsMap[translation.id] = translation
      })
      setTranslatedTranscripts(translationsMap)
      setHasTranslations(true)
      setIsTranslationMode(true) // Auto-switch to translation mode after successful translation
    },
  })

  const handleTranslate = () => {
    if (!hasTranslations) {
      extractTranslation()
    }
    else {
      // Toggle between original and translated
      setIsTranslationMode(!isTranslationMode)
    }
  }

  const handleSetFeedbackStatus = async (txId, status) => {
    setTranscripts((prevTranscript) => {
      return prevTranscript.map((item) => {
        if (item.id === txId) {
          return { ...item, status: item.status === status ? null : status }
        }
        else {
          return item
        }
      })
    })

    if (status !== 'THUMB_UP' && status !== 'THUMB_DOWN')
      return

    const s = transcripts.find(doc => doc.id === txId).status === status ? null : status
    await fetchAndUpdateTranscriptStatus(callId, txId, s)
  }

  const processMessageText = (text) => {
    const interruptionPattern = /<block interruptions enabled. this message was ignored>/i
    const hasInterruption = interruptionPattern.test(text)
    const cleanedText = hasInterruption
      ? text.replace(/<block interruptions enabled. this message was ignored>/gi, '').trim()
      : text
    return { cleanedText, hasInterruption }
  }

  const getDisplayText = (entry) => {
    if (isTranslationMode && hasTranslations && translatedTranscripts[entry.id]) {
      return translatedTranscripts[entry.id].translated
    }
    return entry.text
  }

  const getDetectedLanguage = (entry) => {
    if (hasTranslations && translatedTranscripts[entry.id]) {
      return translatedTranscripts[entry.id].detectedLanguage
    }
    return null
  }

  return (
    <>
      {loadingTranscript
        ? (
            <Loading loading={loadingTranscript} />
          )
        : transcripts?.length > 0
          ? (
              <div className="relative">
                <div className="mb-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {isTranslationMode && hasTranslations && (
                      <span className="text-xs text-gray-500">Showing English translation</span>
                    )}
                  </div>
                  <div className="ml-auto flex items-center gap-2">
                    {showTranslateButton && (
                      <Button onClick={handleTranslate} disabled={loadingTranslating} variant="secondary" size="sm">
                        <Languages size={14} />
                        {loadingTranslating
                          ? 'Translating...'
                          : hasTranslations
                            ? isTranslationMode
                              ? 'Show Original'
                              : 'Show Translation'
                            : 'Translate to English'}
                      </Button>
                    )}
                  </div>
                </div>
                <div
                  className={`
                    flex h-auto flex-col gap-y-2.5 overflow-auto
                    transition-[height] duration-300
                  `}
                >
                  {transcripts.map((entry, index) => {
                    const displayText = getDisplayText(entry)
                    const { cleanedText, hasInterruption } = processMessageText(displayText)
                    const detectedLanguage = getDetectedLanguage(entry)

                    return (
                      <div
                        key={entry.id}
                        className={`
                          flex
                          ${entry.user.toLowerCase() === 'user'
                        ? `justify-start`
                        : `justify-end`}
                        `}
                      >
                        <div
                          className={`
                            max-w-[85%]
                            ${entry.user.toLowerCase() === 'user'
                        ? `bg-gray-100`
                        : `bg-blue-50`}
                            rounded-md p-2 shadow-xs
                          `}
                        >
                          {entry.user.toLowerCase() !== 'user' && (
                            <div
                              className={`
                                mb-0.5 flex items-center justify-between
                              `}
                            >
                              <span
                                className={`
                                  rounded-sm bg-blue-100 px-1.5 py-px text-xs
                                  font-medium text-blue-800
                                `}
                              >
                                Bland
                              </span>
                            </div>
                          )}

                          <div className="relative">
                            {hasInterruption && (
                              <Tooltip
                                content="This message was skipped due to 'Block Interruptions' being enabled"
                                color="default"
                                placement="bottom"
                                delay={0}
                                closeDelay={100}
                                className="mb-1.5"
                              >
                                <div
                                  className={`
                                    flex cursor-default items-center gap-1
                                    text-xs text-gray-500
                                  `}
                                >
                                  <AlertCircle
                                    size={12}
                                    className="text-gray-400"
                                  />
                                  <span>Skipped</span>
                                </div>
                              </Tooltip>
                            )}
                            <p className="text-xs text-gray-800" style={{ fontSize: 13 }}>
                              {cleanedText}
                            </p>
                          </div>
                          {entry.user.toLowerCase() !== 'user' && (
                            <div className="mt-1.5 flex items-center gap-x-1.5">
                              <button
                                type="button"
                                onClick={() => handleSetFeedbackStatus(entry.id, 'THUMB_UP')}
                                className="focus:outline-hidden"
                              >
                                <ThumbsUp size={14} color={entry.status === 'THUMB_UP' ? '#3b82f6' : '#9ca3af'} />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleSetFeedbackStatus(entry.id, 'THUMB_DOWN')}
                                className="focus:outline-hidden"
                              >
                                <ThumbsDown size={14} color={entry.status === 'THUMB_DOWN' ? '#ef4444' : '#9ca3af'} />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          : (
              <div
                className={`
                  flex items-center justify-center rounded-md bg-gray-50 p-2.5
                `}
              >
                <p className="text-xs text-gray-500">No transcript available</p>
              </div>
            )}
    </>
  )
}
